import * as React from 'react';

import {
  enumDocumentType,
  DocumentType,
} from '@rematter/frontend-lib-api-client';
import { Checkbox } from '@rematter/ui-web';
import { cn } from '@rematter/ui-web/lib';
import startCase from 'lodash/startCase';

import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import { LoadRecord } from '~/modules/trade/types/load';

interface Props {
  load: Pick<Partial<LoadRecord>, 'loadNumber' | 'invoiceId'> & { id: string };
  selectedDocuments: Record<string, Record<DocumentType, boolean>>;
  onLoadSelect: (loadId: string, checked: boolean) => void;
  onDocumentSelect: (
    loadId: string,
    documentType: DocumentType,
    checked: boolean,
  ) => void;
}

const DOCUMENT_TYPES = [
  enumDocumentType.Invoice,
  enumDocumentType.BillOfLading,
  enumDocumentType.PackingList,
];

export const SalesOrderTabDocumentsTableRow: React.FC<Props> = ({
  load,
  selectedDocuments,
  onLoadSelect,
  onDocumentSelect,
}) => {
  const t = useTranslations();

  const isAllSelected = DOCUMENT_TYPES.every(
    (type) => selectedDocuments[load.id]?.[type],
  );
  const isSomeSelected = Object.values(selectedDocuments[load.id] ?? {}).some(
    (checked) => checked,
  );

  const getDocumentTypeName = (type: DocumentType) => {
    const key = type
      .toLowerCase()
      .replace(/([A-Z])/g, (match) => match.toLowerCase()) as keyof typeof t;
    return t.bookingDetail.documents.types[key as never] || startCase(type);
  };

  const getColumnWidth = (docType: DocumentType) => {
    switch (docType) {
      case enumDocumentType.BillOfLading:
      case enumDocumentType.PackingList:
        return 'w-[180px]';
      case enumDocumentType.Invoice:
        return 'w-[140px]';
      default:
        return 'w-[140px]';
    }
  };

  return (
    <div className="@SalesOrderTabDocumentsTableRow flex w-full flex-row items-center gap-4 py-2">
      <div
        className="flex h-[45px] w-[150px] cursor-pointer items-center gap-2 rounded-md bg-neutral/5 p-3"
        onClick={() => onLoadSelect(load.id, !isAllSelected)}
      >
        <Checkbox
          isSelected={isAllSelected}
          isIndeterminate={isSomeSelected && !isAllSelected}
          className="pointer-events-none"
        >
          <span className="text-base font-medium">
            {load.loadNumber || 'N/A'}
          </span>
        </Checkbox>
      </div>
      {DOCUMENT_TYPES.map((type) => (
        <div
          key={type}
          className={cn(
            'flex h-[45px] cursor-pointer items-center gap-2 rounded-md border p-3',
            getColumnWidth(type),
            selectedDocuments[load.id]?.[type]
              ? 'border-[2px] border-primary/80 shadow-[0_0_0_2px_var(--color-primary/20)]'
              : 'border-neutral/10',
            type === enumDocumentType.Invoice &&
              !load.invoiceId &&
              'cursor-not-allowed opacity-50',
          )}
          onClick={() =>
            type === enumDocumentType.Invoice && !load.invoiceId
              ? null
              : onDocumentSelect(
                  load.id,
                  type,
                  !selectedDocuments[load.id]?.[type],
                )
          }
        >
          <Checkbox
            isSelected={selectedDocuments[load.id]?.[type] || false}
            className="pointer-events-none"
            disabled={type === enumDocumentType.Invoice && !load.invoiceId}
          >
            <div className="text-base text-neutral/80">
              {getDocumentTypeName(type)}
            </div>
          </Checkbox>
        </div>
      ))}
    </div>
  );
};
